import { Trash2 } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import ConfirmationModal from "../../../../components/ConfirmationModal";
import citriniService from "../../../../services/citriniService";
import { CitriniMappingItem, PagedResponse } from "../../../../types/citrini";
import {
  EmptyState,
  ErrorBanner,
  formatDateCell,
  LoadingSpinner,
  Pagination,
} from "../helpers";
import ProductDetailModal from "./ProductDetailModal";

interface MappingsTableProps {
  isDarkMode: boolean;
}

const MappingsTable: React.FC<MappingsTableProps> = ({ isDarkMode }) => {
  const [data, setData] = useState<PagedResponse<CitriniMappingItem> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [productTypeFilter, setProductTypeFilter] = useState<"" | "BASKET" | "PORTFOLIO">("");
  const [citriniIdFilter, setCitriniIdFilter] = useState("");
  const [providerIdFilter, setProviderIdFilter] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<CitriniMappingItem | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [productModal, setProductModal] = useState<{ productId: number; productType: "BASKET" | "PORTFOLIO"; providerId: number } | null>(null);
  const [nameMap, setNameMap] = useState<Record<string, string>>({});

  const nameMapRef = React.useRef<Record<string, string>>({});

  const resolveNames = useCallback(async (items: CitriniMappingItem[]) => {
    const missing = items.filter(
      (item) => !(item.citriniId in nameMapRef.current)
    );
    if (missing.length === 0) return;

    await Promise.all(
      missing.map(async (item) => {
        try {
          const fetcher =
            item.productType === "BASKET"
              ? citriniService.getLatestBasketByCitriniId(item.citriniId)
              : citriniService.getLatestPortfolioByCitriniId(item.citriniId);
          const result = await fetcher;
          nameMapRef.current[item.citriniId] = result.name;
        } catch {
          // Not found or error -- mark as resolved so we don't retry
          nameMapRef.current[item.citriniId] = "";
        }
      })
    );
    setNameMap({ ...nameMapRef.current });
  }, []);

  const load = useCallback(
    async (p: number) => {
      try {
        setLoading(true);
        setError(null);
        const res = await citriniService.listMappings({
          page: p,
          pageSize: 20,
          orderDir: "desc",
          ...(productTypeFilter ? { productType: productTypeFilter } : {}),
          ...(citriniIdFilter ? { citriniId: citriniIdFilter } : {}),
          ...(providerIdFilter ? { providerId: Number(providerIdFilter) } : {}),
        });
        setData(res);
        setPage(p);
        resolveNames(res.items);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load mappings");
      } finally {
        setLoading(false);
      }
    },
    [productTypeFilter, citriniIdFilter, providerIdFilter, resolveNames]
  );

  useEffect(() => {
    load(1);
  }, [load]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      setDeleteLoading(true);
      await citriniService.deleteMapping(deleteTarget.id);
      setDeleteTarget(null);
      await load(page);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete mapping");
      setDeleteTarget(null);
    } finally {
      setDeleteLoading(false);
    }
  }, [deleteTarget, load, page]);

  return (
    <div>
      {/* Filters */}
      <div className="mb-4 flex flex-wrap gap-3">
        <select
          value={productTypeFilter}
          onChange={(e) => setProductTypeFilter(e.target.value as "" | "BASKET" | "PORTFOLIO")}
          className={`text-sm rounded-lg border px-3 py-1.5 ${
            isDarkMode
              ? "bg-rp-surface border-rp-separator text-rp-grey/70"
              : "bg-white border-gray-300 text-gray-700"
          }`}
        >
          <option value="">All types</option>
          <option value="BASKET">Basket</option>
          <option value="PORTFOLIO">Portfolio</option>
        </select>
        <input
          type="text"
          placeholder="Citrini ID"
          value={citriniIdFilter}
          onChange={(e) => setCitriniIdFilter(e.target.value)}
          className={`text-sm rounded-lg border px-3 py-1.5 w-40 ${
            isDarkMode
              ? "bg-rp-surface border-rp-separator text-rp-grey/70 placeholder-rp-grey/40"
              : "bg-white border-gray-300 text-gray-700 placeholder-gray-400"
          }`}
        />
        <input
          type="text"
          placeholder="Provider ID"
          value={providerIdFilter}
          onChange={(e) => setProviderIdFilter(e.target.value)}
          className={`text-sm rounded-lg border px-3 py-1.5 w-32 ${
            isDarkMode
              ? "bg-rp-surface border-rp-separator text-rp-grey/70 placeholder-rp-grey/40"
              : "bg-white border-gray-300 text-gray-700 placeholder-gray-400"
          }`}
        />
      </div>

      {loading && !data && <LoadingSpinner isDarkMode={isDarkMode} />}
      {error && !data && (
        <ErrorBanner message={error} onRetry={() => load(1)} isDarkMode={isDarkMode} />
      )}
      {data && data.items.length === 0 && (
        <EmptyState message="No mappings found" isDarkMode={isDarkMode} />
      )}

      {error && data && (
        <div
          className={`mb-4 p-3 rounded-lg text-sm ${
            isDarkMode
              ? "bg-red-900/20 border border-red-800 text-red-400"
              : "bg-red-50 border border-red-200 text-red-700"
          }`}
        >
          {error}
        </div>
      )}

      {data && data.items.length > 0 && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr
                  className={`text-left text-xs ${
                    isDarkMode
                      ? "text-rp-grey/50 border-b border-rp-separator"
                      : "text-gray-500 border-b border-gray-200"
                  }`}
                >
                  <th className="pb-2 pr-4 font-medium">ID</th>
                  <th className="pb-2 pr-4 font-medium">Type</th>
                  <th className="pb-2 pr-4 font-medium">Citrini ID</th>
                  <th className="pb-2 pr-4 font-medium">Name</th>
                  <th className="pb-2 pr-4 font-medium">Provider ID</th>
                  <th className="pb-2 pr-4 font-medium">Product ID</th>
                  <th className="pb-2 pr-4 font-medium">Created At</th>
                  <th className="pb-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody
                className={`divide-y ${isDarkMode ? "divide-rp-separator" : "divide-gray-200"}`}
              >
                {data.items.map((item) => (
                  <tr
                    key={item.id}
                    className={`${isDarkMode ? "hover:bg-rp-surface/50" : "hover:bg-gray-50"}`}
                  >
                    <td
                      className={`py-2.5 pr-4 font-mono text-xs ${
                        isDarkMode ? "text-rp-grey/70" : "text-gray-700"
                      }`}
                    >
                      {item.id}
                    </td>
                    <td className="py-2.5 pr-4">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                          item.productType === "BASKET"
                            ? "bg-purple-100 text-purple-800"
                            : "bg-indigo-100 text-indigo-800"
                        }`}
                      >
                        {item.productType}
                      </span>
                    </td>
                    <td
                      className={`py-2.5 pr-4 font-mono text-xs ${
                        isDarkMode ? "text-rp-grey/70" : "text-gray-700"
                      }`}
                    >
                      {item.citriniId}
                    </td>
                    <td
                      className={`py-2.5 pr-4 text-xs ${
                        isDarkMode ? "text-rp-grey" : "text-gray-800"
                      }`}
                    >
                      {nameMap[item.citriniId] || "-"}
                    </td>
                    <td
                      className={`py-2.5 pr-4 font-mono text-xs ${
                        isDarkMode ? "text-rp-grey/50" : "text-gray-500"
                      }`}
                    >
                      {item.providerId}
                    </td>
                    <td className="py-2.5 pr-4">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setProductModal({ productId: item.productId, productType: item.productType, providerId: item.providerId });
                        }}
                        className={`font-mono text-xs underline ${
                          isDarkMode
                            ? "text-blue-400 hover:text-blue-300"
                            : "text-blue-600 hover:text-blue-800"
                        }`}
                      >
                        {item.productId}
                      </button>
                    </td>
                    <td
                      className={`py-2.5 pr-4 ${
                        isDarkMode ? "text-rp-grey/70" : "text-gray-700"
                      }`}
                    >
                      {formatDateCell(item.createdAtUtc)}
                    </td>
                    <td className="py-2.5">
                      <button
                        onClick={() => setDeleteTarget(item)}
                        className={`p-1 rounded transition-colors ${
                          isDarkMode
                            ? "hover:bg-red-900/30 text-rp-grey/50 hover:text-red-400"
                            : "hover:bg-red-50 text-gray-400 hover:text-red-600"
                        }`}
                        title="Delete mapping (forces re-sync on next run)"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={page}
            totalPages={data.totalPages}
            totalItems={data.totalItems}
            onPageChange={load}
            isDarkMode={isDarkMode}
          />
        </>
      )}

      <ConfirmationModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Mapping"
        message={
          deleteTarget
            ? `This will delete mapping #${deleteTarget.id} (${deleteTarget.productType} "${deleteTarget.citriniId}" -> Product ${deleteTarget.productId}). The sync job will recreate the product on the next run. This cannot be undone.`
            : ""
        }
        confirmText="Delete"
        isDestructive
        loading={deleteLoading}
      />

      <ProductDetailModal
        isOpen={productModal !== null}
        onClose={() => setProductModal(null)}
        productId={productModal?.productId ?? 0}
        productType={productModal?.productType ?? "BASKET"}
        providerId={productModal?.providerId ?? 0}
      />
    </div>
  );
};

export default MappingsTable;
